import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GearIcon, TrashSimpleIcon, BroomIcon } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { listPendingDeletions, markDeletionProcessed, clearPendingDeletions } from "@/lib/api";

export default function AdminAuditDialog() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await listPendingDeletions();
      setItems(d.items || []); setCount(d.count || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const mark = async (eid) => { await markDeletionProcessed(eid); toast.success("Marked processed"); load(); };
  const clearAll = async () => { const r = await clearPendingDeletions(); toast.success(`Cleared ${r.deleted}`); load(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none h-9 font-mono text-[11px] uppercase tracking-[0.18em]" data-testid="admin-audit-btn">
          <GearIcon size={14} className="mr-2" /> Storage Audit
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none max-w-3xl" data-testid="admin-audit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Storage Audit · Pending Deletions</DialogTitle>
          <DialogDescription>
            Emergent object storage has no delete API, so orphaned file paths are logged here
            when an ingredient or document is removed. Mark entries processed once your ops
            team (or a future cleanup job) has physically deleted the underlying blobs.
          </DialogDescription>
        </DialogHeader>

        <div className="font-mono text-xs text-[var(--text-muted)]">
          {loading ? "Loading…" : `${count} pending entr${count === 1 ? "y" : "ies"}`}
        </div>

        {items.length > 0 ? (
          <div className="border border-[var(--border)] max-h-[420px] overflow-auto" data-testid="pending-list">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  {["PATH", "REASON", "WHEN", ""].map((h, i) => (
                    <th key={i} className="label-kicker text-left px-3 py-2 border-b border-[var(--border)] bg-[var(--surface)]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="cell-border" data-testid={`pending-${it.id}`}>
                    <td className="px-3 py-2 font-mono text-[10px] break-all">{it.storage_path}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[var(--text-muted)]">{it.context?.reason}</td>
                    <td className="px-3 py-2 font-mono text-[10px]">{new Date(it.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <button className="text-[var(--text-muted)] hover:text-[var(--danger)]" onClick={() => mark(it.id)} data-testid={`pending-mark-${it.id}`}>
                        <TrashSimpleIcon size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : !loading ? (
          <div className="border border-dashed border-[var(--border)] p-8 text-center font-mono text-xs text-[var(--text-muted)]" data-testid="pending-empty">
            No pending deletions. Your storage audit log is clean.
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-none">Close</Button>
          <Button onClick={clearAll} disabled={!count} className="rounded-none bg-[var(--danger)] hover:bg-[var(--danger)]/90 text-white" data-testid="pending-clear-all">
            <BroomIcon size={14} className="mr-2" /> Clear All
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
